import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { ReloadOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Modal, Select, Tooltip, notification } from "antd";
import clsx from "clsx";
import { journalsQueries, useJournalMutation, TitlePageFillersDropdown } from "@/entities/journal";
import { DownloadIcon, VectorIcon } from "@/shared/assets/svg/icons";
import { useNavigate } from "@/shared/lib/react-router";

import "./ojr-formation-panel.scss";

const MOCK_SUPERVISION_NOTIFICATION_URL =
  "/projects/4db3f1ee-b051-4904-aa3f-39f852a877c6/supervision/8cf4c8ac-0a57-4d27-ad06-f8b4fa06ffb6/notifications/?tab=processing&notificationId=processing-1";

type DemoState =
  | ""
  | "idle"
  | "formed"
  | "sent"
  | "error"
  | "no_integration";

const DEMO_OPTIONS: { value: DemoState; label: string }[] = [
  { value: "", label: "По API" },
  { value: "idle", label: "Только кнопка" },
  { value: "formed", label: "Сформирован" },
  { value: "sent", label: "Отправлен" },
  { value: "error", label: "Ошибка интеграции" },
  { value: "no_integration", label: "Нет интеграции" },
];

type OjrFormationPanelProviderProps = {
  titleId: string;
  objectId: string;
  mainJournalId: string;
  titleStatus?: string;
  children: ReactNode;
};

const DISPLAY_RESULT_STATUSES = new Set([
  "SIGNED",
  "SENT_TO_GSN",
  "INTERACTION_ERROR_WITH_GSN",
  "COMMENTS_FROM_GSN",
  "REGISTERED_WITH_GSN",
]);

const formatMoscowDateTime = (date: Date) =>
  date.toLocaleString("ru-RU", {
    timeZone: "Europe/Moscow",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getCounterValue = (value?: number | null) => value ?? 0;
const SENT_TITLE_STATUSES = new Set([
  "SENT_TO_GSN",
  "REGISTERED_WITH_GSN",
]);

type OjrFormationPanelContextValue = {
  demoState: DemoState;
  setDemoState: (s: DemoState) => void;
  showResultBlock: boolean;
  isFormModalOpen: boolean;
  setIsFormModalOpen: (v: boolean) => void;
  isReformModalOpen: boolean;
  setIsReformModalOpen: (v: boolean) => void;
  isExpanded: boolean;
  setIsExpanded: (v: boolean | ((prev: boolean) => boolean)) => void;
  isPrepareLoading: boolean;
  isSendLoading: boolean;
  isSendModalOpen: boolean;
  setIsSendModalOpen: (v: boolean) => void;
  handleForm: () => Promise<void>;
  handleSendToGsn: () => Promise<void>;
  handleDownloadPackage: () => void;
  handleOpenSupervisionCase: () => void;
  shouldHideSendButton: boolean;
  isSentToGsn: boolean;
  resultDescription: string;
  currentJournal: any;
  lastFormedAt: Date | null;
  formatMoscowDateTime: (d: Date) => string;
  getCounterValue: (v?: number | null) => number;
  showNoIntegrationBanner: boolean;
  showIntegrationErrorBanner: boolean;
};

const OjrFormationPanelContext = createContext<OjrFormationPanelContextValue | null>(null);

function useOjrFormationPanel() {
  const ctx = useContext(OjrFormationPanelContext);
  if (!ctx) throw new Error("OjrFormationPanelTopRow/Content must be used inside OjrFormationPanelProvider");
  return ctx;
}

export function OjrFormationPanelProvider({
  titleId,
  objectId,
  mainJournalId,
  titleStatus,
  children,
}: OjrFormationPanelProviderProps) {
  const queryClient = useQueryClient();

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isReformModalOpen, setIsReformModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [lastFormedAt, setLastFormedAt] = useState<Date | null>(null);
  const [isLocallyFormed, setIsLocallyFormed] = useState(false);
  const [isPrepareLoading, setIsPrepareLoading] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [demoState, setDemoState] = useState<DemoState>("");

  const { data: journalsData } = useQuery(
    journalsQueries.getDraftJournals({
      buildingObjectId: objectId,
      page: 1,
      limit: 100,
    }),
  );

  const currentJournal = useMemo(
    () =>
      journalsData?.buildingObject?.mainJournals?.find(
        (journal) => journal.mainJournalId === mainJournalId,
      ),
    [journalsData, mainJournalId],
  );

  const isIntegrationEnabledRaw =
    journalsData?.buildingObject?.gsnIntegrationSettings?.isEnabled ?? false;

  let showResultBlock =
    isLocallyFormed || (titleStatus ? DISPLAY_RESULT_STATUSES.has(titleStatus) : false);
  let isSentToGsn = titleStatus ? SENT_TITLE_STATUSES.has(titleStatus) : false;
  let hasInteractionError = titleStatus === "INTERACTION_ERROR_WITH_GSN";
  let isIntegrationEnabled = isIntegrationEnabledRaw;

  if (demoState) {
    showResultBlock = ["formed", "sent", "error", "no_integration"].includes(demoState);
    isSentToGsn = demoState === "sent";
    hasInteractionError = demoState === "error";
    isIntegrationEnabled = demoState !== "no_integration";
  }

  const { mutateAsync: prepareFilesReq } = useJournalMutation(
    "post",
    "/api/v1/journals/title/{titleId}/prepareFilesRq",
  );

  const { mutateAsync: sendToGsn } = useJournalMutation(
    "post",
    "/api/v1/journals/title/{titleId}/status/send",
  );

  const refreshJournalData = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: journalsQueries.getDraftJournals({
          buildingObjectId: objectId,
        }).queryKey,
      }),
    ]);
  }, [queryClient, titleId, objectId]);

  const handleForm = useCallback(async () => {
    setIsPrepareLoading(true);
    try {
      await prepareFilesReq({
        params: {
          path: { titleId },
        },
      } as any);

      setLastFormedAt(new Date());
      setIsLocallyFormed(true);
      setIsFormModalOpen(false);
      setIsReformModalOpen(false);
      setIsExpanded(true);

      notification.success({
        message: "ОЖР успешно сформирован",
        description:
          'Перейдите в журнал "Название журнала" и сможете его скачать или отправить в орган Надзора.',
        placement: "topRight",
      });

      await refreshJournalData();
    } catch {
      notification.error({
        message: "Ошибка при формировании ОЖР",
        description:
          "Повторите формирование или обратитесь к администратору проекта.",
        placement: "topRight",
      });
      // Временно: при отсутствии API показываем успех следом, модалку не закрываем
      notification.success({
        message: "ОЖР успешно сформирован",
        description:
          'Перейдите в журнал "Название журнала" и сможете его скачать или отправить в орган Надзора.',
        placement: "topRight",
      });
    } finally {
      setIsPrepareLoading(false);
    }
  }, [titleId, prepareFilesReq, refreshJournalData]);

  const handleSendToGsn = useCallback(async () => {
    setIsSendLoading(true);
    try {
      await sendToGsn({
        params: {
          path: { titleId },
        },
      } as any);

      notification.success({
        message: "Журнал успешно отправлен в орган Надзора",
        description: "Сообщение отправки появится в модуле Надзорное дело.",
        placement: "topRight",
      });
      setIsSendModalOpen(false);
      await refreshJournalData();
    } catch {
      notification.error({
        message: "Ошибка взаимодействия",
        description: "Не удалось отправить ОЖР в орган Надзора. Повторите отправку.",
        placement: "topRight",
      });
    } finally {
      setIsSendLoading(false);
    }
  }, [titleId, sendToGsn, refreshJournalData]);

  const handleDownloadPackage = useCallback(() => {
    notification.info({
      message: "Формирование выгрузки",
      description:
        "Выгрузка пакета станет доступна позже",
      placement: "topRight",
    });
  }, []);

  const navigate = useNavigate();
  const handleOpenSupervisionCase = useCallback(() => {
    navigate(MOCK_SUPERVISION_NOTIFICATION_URL);
  }, [navigate]);

  const shouldHideSendButton = !isIntegrationEnabled || isSentToGsn;
  const showNoIntegrationBanner = showResultBlock && !isIntegrationEnabled;
  const showIntegrationErrorBanner =
    showResultBlock && !showNoIntegrationBanner && hasInteractionError;

  const resultDescription = isSentToGsn
    ? "ОЖР успешно отправлен в орган Надзора. Можно открыть сообщение об отправке в Надзорном деле."
    : "ОЖР успешно сформирован по требованиям Универсальной Интеграционной Шины Стройформ в рамках настроек интеграции с органом строительного Надзора и экспертизы Санкт-Петербурга. Вы можете скачать его и отправить в орган Надзора. Также если вы внесете изменения в ОЖР, можно сформировать его заново с внесенными изменениями.";

  const contextValue = useMemo<OjrFormationPanelContextValue>(
    () => ({
    demoState,
    setDemoState,
    showResultBlock,
    isFormModalOpen,
    setIsFormModalOpen,
    isReformModalOpen,
    setIsReformModalOpen,
    isExpanded,
    setIsExpanded,
    isPrepareLoading,
    isSendLoading,
    isSendModalOpen,
    setIsSendModalOpen,
    handleForm,
    handleSendToGsn,
    handleDownloadPackage,
    handleOpenSupervisionCase,
    shouldHideSendButton,
    isSentToGsn,
    resultDescription,
    currentJournal,
    lastFormedAt,
    formatMoscowDateTime,
    getCounterValue,
    showNoIntegrationBanner,
    showIntegrationErrorBanner,
    }),
    [
      demoState,
      setDemoState,
      showResultBlock,
      isFormModalOpen,
      setIsFormModalOpen,
      isReformModalOpen,
      setIsReformModalOpen,
      isExpanded,
      setIsExpanded,
      isPrepareLoading,
      isSendLoading,
      isSendModalOpen,
      setIsSendModalOpen,
      handleForm,
      handleSendToGsn,
      handleDownloadPackage,
      handleOpenSupervisionCase,
      shouldHideSendButton,
      isSentToGsn,
      resultDescription,
      currentJournal,
      lastFormedAt,
      showNoIntegrationBanner,
      showIntegrationErrorBanner,
    ],
  );

  return (
    <OjrFormationPanelContext.Provider value={contextValue}>
      {children}
    </OjrFormationPanelContext.Provider>
  );
}

export function OjrFormationPanelTopRow() {
  const ctx = useOjrFormationPanel();
  const {
    demoState,
    setDemoState,
    showResultBlock,
    setIsReformModalOpen,
    setIsFormModalOpen,
  } = ctx;
  return (
    <div className="ojr-formation-panel__top-row">
      <div className="ojr-formation-panel__demo">
        <span className="ojr-formation-panel__demo-label">Демо:</span>
        <Select<DemoState>
          value={demoState}
          onChange={setDemoState}
          options={DEMO_OPTIONS}
          style={{ width: 180 }}
          placeholder="По API"
        />
      </div>
      <Tooltip
        title="Формирование пакета документа ОЖР: каждое формирование создает новую версию и позволяет отправить ОЖР в орган Надзора (если интеграция подключена)."
        placement="bottom"
      >
        <Button
          type="primary"
          icon={showResultBlock ? <ReloadOutlined /> : undefined}
          onClick={
            showResultBlock
              ? () => setIsReformModalOpen(true)
              : () => setIsFormModalOpen(true)
          }
        >
          {showResultBlock ? "Сформировать" : "Сформировать пакет"}
        </Button>
      </Tooltip>
    </div>
  );
}

export function OjrFormationPanelContent() {
  const ctx = useOjrFormationPanel();
  const {
    showResultBlock,
    isExpanded,
    setIsExpanded,
    resultDescription,
    shouldHideSendButton,
    isSentToGsn,
    isSendModalOpen,
    setIsSendModalOpen,
    isSendLoading,
    handleSendToGsn,
    handleDownloadPackage,
    handleOpenSupervisionCase,
    isFormModalOpen,
    setIsFormModalOpen,
    isPrepareLoading,
    handleForm,
    lastFormedAt,
    formatMoscowDateTime,
    currentJournal,
    getCounterValue,
    showNoIntegrationBanner,
    showIntegrationErrorBanner,
    isReformModalOpen,
    setIsReformModalOpen,
  } = ctx;

  return (
    <div className="ojr-formation-panel">
      {showResultBlock ? (
        <div className="ojr-formation-panel__result">
          <button
            type="button"
            className="ojr-formation-panel__result-head"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            <span className="ojr-formation-panel__result-title">
              Формирование и отправка ОЖР
            </span>
            <span
              className={clsx("ojr-formation-panel__result-chevron", !isExpanded && "ojr-formation-panel__result-chevron--down")}
            >
              <VectorIcon />
            </span>
          </button>

          <div
            className={clsx("ojr-formation-panel__result-body-wrap", !isExpanded && "ojr-formation-panel__result-body-wrap--collapsed")}
          >
            <div className="ojr-formation-panel__result-body">
              <div className="ojr-formation-panel__result-content">
                <p className="ojr-formation-panel__description">{resultDescription}</p>
              </div>
              <div className="ojr-formation-panel__buttons">
                {!shouldHideSendButton ? (
                  <Button
                    type="primary"
                    onClick={() => setIsSendModalOpen(true)}
                    loading={isSendLoading}
                  >
                    Отправить в орган Надзора
                  </Button>
                ) : null}
                {isSentToGsn ? (
                  <Button type="primary" onClick={handleOpenSupervisionCase}>
                    Открыть в Надзорном деле
                  </Button>
                ) : null}
                <Button
                  icon={<DownloadIcon />}
                  onClick={handleDownloadPackage}
                >
                  Выгрузить пакет
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <Modal
        open={isFormModalOpen}
        width={768}
        className="ojr-formation-panel__modal ojr-formation-panel__form-modal"
        styles={{ footer: { textAlign: "start" } }}
        title={`Формирование пакета ОЖР от ${formatMoscowDateTime(new Date()).split(", ")[0]}`}
        onCancel={() => setIsFormModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsFormModalOpen(false)}>
            Назад
          </Button>,
          <Button key="confirm" type="primary" onClick={handleForm} loading={isPrepareLoading}>
            Да, сформировать
          </Button>,
        ]}
      >
        <div className="ojr-formation-panel__modal-info">
          <p>
            Перед формированием убедитесь, что данные корректны и имеются в полном
            объеме. Не все записи в разделах подписаны, вы уверены, что хотите
            сформировать ОЖР?
          </p>
          <p>
            В орган Надзора будут отправлены: Титульный лист, список изменений
            Титульного листа, регистрационная запись органа государственного
            строительного надзора, записи 6 разделов ОЖР, прикрепленные документы
            и подписи.
          </p>
        </div>

        <div className="ojr-formation-panel__modal-section">
          <div className="ojr-formation-panel__modal-label">
            Сведения о регистрации титульного листа
          </div>
          <div className="ojr-formation-panel__registration">
            <span>Общие сведения</span>
            <div className="ojr-formation-panel__registration-right">
              <span className="ojr-formation-panel__registration-datetime">
                {currentJournal?.registrationInfo?.buildingInfo?.docDate ? (
                  <>
                    <span className="ojr-formation-panel__registration-date">
                      {formatMoscowDateTime(new Date(currentJournal.registrationInfo.buildingInfo.docDate)).split(", ")[0]}
                    </span>
                    <span className="ojr-formation-panel__registration-time">
                      {" "}
                      {formatMoscowDateTime(new Date(currentJournal.registrationInfo.buildingInfo.docDate)).split(", ")[1]} МСК
                    </span>
                  </>
                ) : (
                  "—"
                )}
              </span>
              <span className="ojr-formation-panel__registration-status">
                Зарегистрирован
              </span>
              <span className="ojr-formation-panel__registration-version">
                Версия №{currentJournal?.registrationInfo?.versionNumber ?? "—"}
              </span>
            </div>
          </div>
        </div>

        <div className="ojr-formation-panel__modal-section">
          <div className="ojr-formation-panel__modal-label">Сведения о разделах</div>
          <div className="ojr-formation-panel__sections-grid">
            {[1, 2, 3, 4, 5].map((sectionNumber) => {
              const sectionKey = `section${sectionNumber}` as
                | "section1"
                | "section2"
                | "section3"
                | "section4"
                | "section5";
              const section = currentJournal?.[sectionKey];

              return (
                <div key={sectionNumber} className="ojr-formation-panel__section-card">
                  <div className="ojr-formation-panel__section-title">
                    Раздел №{sectionNumber}
                  </div>
                  <div className="ojr-formation-panel__section-row">
                    Подписано{" "}
                    <span className="ojr-formation-panel__section-count ojr-formation-panel__section-count--signed">
                      {getCounterValue(section?.signed)}
                    </span>
                  </div>
                  <div className="ojr-formation-panel__section-row ojr-formation-panel__section-row--muted">
                    Ожидает{" "}
                    <span className="ojr-formation-panel__section-count">
                      {getCounterValue(section?.awaiting)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      <Modal
        open={isSendModalOpen}
        className="ojr-formation-panel__modal"
        styles={{ footer: { textAlign: "start" } }}
        title="Подписать и отправить ОЖР"
        onCancel={() => setIsSendModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsSendModalOpen(false)}>
            Отмена
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={isSendLoading}
            onClick={handleSendToGsn}
          >
            Подписать и отправить
          </Button>,
        ]}
      >
        <div className="ojr-formation-panel__modal-info">
          Будет выполнено подписание и отправка пакета ОЖР в орган Надзора.
        </div>
        <div className="ojr-formation-panel__send-docs">
          <div className="ojr-formation-panel__send-docs-title">
            В состав отправки входят XML-документы и подписи:
          </div>
          <div className="ojr-formation-panel__send-docs-list">
            Participants.xml, BuildingParticipants.xml, GeneralWorkJournalTitlePage.xml,
            BuildingInfo.xml, WorkInfo.xml, sk5111.xml, gsn35.xml, GeneralWorkJournal.xml
            и .sig подписи к каждому XML.
          </div>
        </div>
      </Modal>

      <Modal
        open={isReformModalOpen}
        className="ojr-formation-panel__modal"
        styles={{ footer: { textAlign: "start" } }}
        title="Сформировать заново?"
        onCancel={() => setIsReformModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsReformModalOpen(false)}>
            Нет, отменить
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleForm}
            loading={isPrepareLoading}
          >
            Да, подтвердить
          </Button>,
        ]}
      >
        <div className="ojr-formation-panel__modal-info">
          Будет собран новый пакет документов ОЖР с учётом изменений, которые были внесены.
        </div>
        <div className="ojr-formation-panel__reform-date">
          Дата формирования - {formatMoscowDateTime(lastFormedAt || new Date())}
        </div>
      </Modal>

      {showNoIntegrationBanner ? (
        <div className="ojr-formation-panel__integration-hint">
          {/* <InfoCircleOutlined /> */}
          Настройка интеграции с органом надзора отсутствует. Обратитесь к{" "}
          <TitlePageFillersDropdown text=" Обратитесь к администратору" />.
        </div>
      ) : null}
      {showIntegrationErrorBanner ? (
        <div className="ojr-formation-panel__integration-hint">
          {/* <InfoCircleOutlined /> */}
          Ошибка интеграции, невозможно отправить в орган Надзора. {" "}
          <TitlePageFillersDropdown text="Обратитесь к администратору" />.

        </div>
      ) : null}
    </div>
  );
}

